import { Mail, MapPin, Phone, Check, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";

const Contact = () => (
  <>
    <SEO title="Contact Us | UMI IT Services Adelaide" description="Book a free 30-minute IT consultation with Adelaide's UMI IT Services, or call 0489 222 864. Plain-English advice, no obligation." />

    <section className="bg-[#F8FAFC] pt-28 pb-16 md:pt-36 md:pb-24">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]">Contact us</p>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1] text-[#0B2545]">
            Book your free consultation
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Tell us a little about your business and what's prompting you to look at IT support. An Adelaide-based specialist will get back to you personally.
          </p>

          <ul className="mt-8 space-y-5">
            <li className="flex items-start gap-3">
              <Phone size={20} className="mt-0.5 shrink-0 text-[#17B8A6]" aria-hidden="true" />
              <div>
                <p className="font-semibold text-[#0B2545]">Call us</p>
                <a href="tel:+61489222864" data-testid="contact-phone-link" className="text-sm text-[#0D9488] font-medium underline">
                  0489 222 864
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <MessageCircle size={20} className="mt-0.5 shrink-0 text-[#17B8A6]" aria-hidden="true" />
              <div>
                <p className="font-semibold text-[#0B2545]">WhatsApp Business</p>
                <a href="https://wa.me/61489222864" target="_blank" rel="noopener" data-testid="contact-whatsapp-link" className="text-sm text-[#0D9488] font-medium underline">
                  Message us on WhatsApp
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={20} className="mt-0.5 shrink-0 text-[#17B8A6]" aria-hidden="true" />
              <div>
                <p className="font-semibold text-[#0B2545]">Prefer email?</p>
                <a href="mailto:maneesh@umi-it.com.au" data-testid="contact-email-link" className="text-sm text-[#0D9488] font-medium underline">
                  maneesh@umi-it.com.au
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={20} className="mt-0.5 shrink-0 text-[#17B8A6]" aria-hidden="true" />
              <div>
                <p className="font-semibold text-[#0B2545]">Service area</p>
                <p className="text-sm text-slate-600">Adelaide CBD and greater metro South Australia. On-site visits available.</p>
              </div>
            </li>
          </ul>

          <div className="mt-8 rounded-xl bg-white border border-slate-200 p-5">
            <p className="text-sm font-semibold text-[#0B2545]">What happens next?</p>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              {["We get back to you quickly", "A relaxed 30-minute chat about your setup", "You get a plain-English summary of your options — yours to keep"].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Check size={15} className="mt-0.5 shrink-0 text-[#17B8A6]" aria-hidden="true" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-10" id="consultation-form">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 md:py-20 bg-white">
      <div className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2545]">Common questions before you get in touch</h2>
        <div className="mt-8 space-y-6">
          {[
            { q: "How much does IT support cost?", a: "Every environment is different, so we don't publish one-size-fits-all pricing. After your free consultation you receive a clear, written proposal with predictable monthly costs — and it's yours to keep either way." },
            { q: "Do you lock clients into contracts?", a: "No. Our agreements are month-to-month. If we ever stop earning your business, you're free to leave with full documentation of your environment." },
            { q: "What size businesses do you specialise in?", a: "We're built for businesses with 5 to 150 staff — professional services, healthcare clinics, logistics, trades and growing family businesses across Adelaide and greater metro South Australia." },
            { q: "Do you only support Adelaide?", a: "Our engineers and on-site coverage are Adelaide-based, from the CBD to Mawson Lakes and Marion. Remote support and cloud services are available to businesses anywhere in Australia." },
          ].map((f) => (
            <div key={f.q} className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-6">
              <h3 className="text-base font-semibold text-[#0B2545]">{f.q}</h3>
              <p className="mt-2 text-base text-slate-600 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default Contact;
